import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule, DatePipe, SlicePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { TippyModule } from '@ngneat/helipopper';
import { SafeUrl } from '@angular/platform-browser';

import { ChatEntity, ChatMessageEntity } from '@core/entities';
import { ChatMessageType, ChatType, SocketEvents } from '@core/enums';
import { DefaultImageDirective, FetchProfileImageDirective } from '@shared/directives';
import { IconComponent, LightboxGalleryComponent } from '@shared/components/ui';
import { FriendlyDatePipe, TruncateTextPipe } from '@shared/pipes';
import { ChatsComposition } from '@shared/compositions/chats.composition';

@UntilDestroy()
@Component({
  selector: 'app-group-chat-box',
  templateUrl: './group-chat-box.component.html',
  styleUrls: ['./group-chat-box.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    NgxSkeletonLoaderModule,
    IconComponent,
    FetchProfileImageDirective,
    DefaultImageDirective,
    DatePipe,
    SlicePipe,
    FriendlyDatePipe,
    TruncateTextPipe,
    TippyModule,
    LightboxGalleryComponent,
  ],
})
export class GroupChatBoxComponent extends ChatsComposition implements OnInit, OnDestroy {
  @Input() groupId!: number;
  @Input() groupName = '';
  @Input() groupImageUrl: string | SafeUrl = '';
  @Input() showHeader = true;

  // ViewChild for scroll container - name must be _myScrollContainer to work with composition's _scrollToBottom
  @ViewChild('chatPanel') _myScrollContainer!: ElementRef;

  protected override readonly ChatMessageType = ChatMessageType;
  protected readonly Math = Math;

  ngOnInit(): void {
    if (this.groupId) {
      this._initializeGroupChat();
    }
  }

  ngOnDestroy(): void {
    // Clean up socket listeners
    this._socketService.removeAllListeners(SocketEvents.ChatRoomData);
    this._socketService.removeAllListeners(SocketEvents.ChatFirstUserMessage);
  }

  removeAttachment(): void {
    this.selectedAttachment = null;
    this.selectedImagePreviewUrl = null;
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------------ Private Methods ----------------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  private _initializeGroupChat(): void {
    // Create a mock ChatEntity for group chat to leverage composition methods
    const groupChatEntity: ChatEntity = {
      id: this.groupId,
      name: this.groupName,
      fullName: this.groupName,
      type: ChatType.GROUP_CHAT,
      count: 0,
      lastMessage: null,
      groupId: this.groupId,
      groupImageUrl: this.groupImageUrl as string,
    } as ChatEntity;

    // Store component reference for scroll functionality
    this._chatComponentInstance = this;

    // Use the composition's selectConversation method which handles:
    // - Setting roomId
    // - Fetching group participants
    // - Joining chat room
    // - Fetching messages
    this.selectConversation(groupChatEntity);

    // Subscribe to new messages - override to only refresh messages, not full conversations
    this._subscribeToGroupChatMessages();
  }

  /**
   * Subscribe to new messages for group chat only.
   * This is a simplified version that doesn't fetch full conversation list.
   */
  private _subscribeToGroupChatMessages(): void {
    this._socketService.on(SocketEvents.ChatFirstUserMessage, () => {
      if (this.roomId && this.selectedConversation) {
        // Only refresh messages, don't fetch full conversations list
        this._useChat
          .getIndividualUserConversationMessages(this.roomId)
          .pipe(untilDestroyed(this))
          .subscribe({
            next: (response: ChatMessageEntity[]) => {
              this.selectedConversationChatMessages = response;
              this._scrollToBottom();
            },
          });
      }
    });
  }
}
